import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { LinkIcon } from 'lucide-react';
import { toast } from 'sonner';
import { applyServerErrors } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { authApi } from '../api';
import { resetPasswordSchema, type ResetPasswordValues } from '../schemas';
import { AuthLayout } from '../components/AuthLayout';

/**
 * Set a new password from an emailed link.
 *
 * The token and the address come from the query string — the person types
 * neither. The server is the only thing that can actually judge the token, so
 * this screen does not pretend to: it checks only that BOTH are present, and
 * anything else is the server's 422.
 *
 * That presence check earns its place. Without it, a link mangled by a mail
 * client — and they do get mangled, which is why the mail prints the URL in
 * full underneath the button — renders a working-looking form that fails only
 * after somebody has chosen and typed a password twice. Failing before the
 * form is kinder than failing after it.
 *
 * There is no auto sign-in on success, and that is not an omission. The reset
 * endpoint deletes every token the account holds, precisely because a reset is
 * often triggered BY a compromise. Handing back a fresh session here would
 * quietly undo the one security property the endpoint exists to provide.
 */
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const token = params.get('token');
  const email = params.get('email');

  const form = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '', password_confirmation: '' },
  });

  const reset = useMutation({
    mutationFn: (values: ResetPasswordValues) =>
      authApi.resetPassword({
        token: token ?? '',
        email: email ?? '',
        password: values.password,
        password_confirmation: values.password_confirmation,
      }),
    onSuccess: () => {
      toast.success('Password changed. Sign in with your new one.');
      navigate('/login', { replace: true });
    },
    onError: (error) => {
      if (!applyServerErrors(error, form.setError as never)) {
        toast.error(
          error instanceof Error ? error.message : 'That reset link is invalid or has expired.',
        );
      }
    },
  });

  if (token === null || token === '' || email === null || email === '') {
    return (
      <AuthLayout
        title="That link is incomplete"
        subtitle="It may have been cut short by your email app when you copied it."
      >
        <div className="flex flex-col gap-5">
          <div className="flex items-start gap-3 rounded-xl border border-border bg-sunken p-4">
            <LinkIcon className="mt-0.5 h-5 w-5 shrink-0 text-ink-muted" strokeWidth={2} aria-hidden />
            <p className="text-sm leading-relaxed text-ink-secondary">
              Open the link from the email again, using the full address printed underneath the
              button. If it still does not work, ask for a fresh one — links expire after an hour.
            </p>
          </div>

          <Button asChild size="lg" className="w-full">
            <Link to="/forgot-password">Send me a new link</Link>
          </Button>

          <p className="text-center text-sm text-ink-secondary">
            <Link
              to="/login"
              className="font-semibold text-ink underline underline-offset-4 hover:text-brand"
            >
              Back to sign in
            </Link>
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle={`Setting a new password for ${email}.`}
      footer={
        <p className="text-sm text-ink-secondary">
          Remembered it?{' '}
          <Link
            to="/login"
            className="font-semibold text-ink underline underline-offset-4 hover:text-brand"
          >
            Back to sign in
          </Link>
        </p>
      }
    >
      <form
        onSubmit={form.handleSubmit((values) => reset.mutate(values))}
        className="flex flex-col gap-4"
        noValidate
      >
        {/* Hidden, and there for password managers rather than for the server —
            they need a username field beside a new-password field to know which
            login they are being asked to update. */}
        <input type="hidden" name="email" autoComplete="username" value={email} readOnly />

        <Field
          label="New password"
          htmlFor="password"
          hint="At least 8 characters"
          error={form.formState.errors.password?.message}
          required
        >
          <PasswordInput
            id="password"
            autoComplete="new-password"
            autoFocus
            aria-invalid={!!form.formState.errors.password}
            {...form.register('password')}
          />
        </Field>

        <Field
          label="Confirm new password"
          htmlFor="password_confirmation"
          error={form.formState.errors.password_confirmation?.message}
          required
        >
          <PasswordInput
            id="password_confirmation"
            autoComplete="new-password"
            aria-invalid={!!form.formState.errors.password_confirmation}
            {...form.register('password_confirmation')}
          />
        </Field>

        <Button type="submit" size="lg" loading={reset.isPending} className="mt-1 w-full">
          Change password
        </Button>

        <p className="text-center text-xs leading-relaxed text-ink-secondary">
          Changing your password signs you out everywhere else.
        </p>
      </form>
    </AuthLayout>
  );
}
